/**
 * Site navigation for the marketing pages: one data source for the header, the footer and the
 * home-page link blocks (T816, audit: docs/reports/site-ia-seo-audit-2026-10-30.md).
 *
 * Owner rules (2026-10-30): the header has a Products menu with the three product pages, a
 * separate Free tools group and Pricing; Compare, FAQ and How it works are not in the header
 * (they are linked from the footer and from page content). Copy: docs/design/landing/copy.md,
 * "T816 navigation". Labels match the target page's topic; never "click here".
 * No email addresses here: the contact link is built from config in the layout.
 */

/** Icon keys of the header menus; site-nav.tsx maps each to a Lucide icon (names also in docs/design/html/icons.json). */
export type NavIcon =
  | "route"
  | "receipt-text"
  | "file-text"
  | "file-check"
  | "file-spreadsheet"
  | "calculator"
  | "arrow-left-right"
  | "clipboard-check"
  | "chart-line"
  | "car"
  | "book-open"
  | "bell"

/** Chip tones of the design system (docs/design/design-system.md rule 4). */
export type NavTone = "primary" | "success" | "warning" | "violet" | "muted"

export interface NavLink {
  label: string
  href: string
  /** One short line under the label in a dropdown or a home-page card. */
  description?: string
  /** Header dropdown only: the tinted icon chip. */
  icon?: NavIcon
  tone?: NavTone
  /** Header dropdown only: shown as the quiet "see all" row under the list. */
  footer?: boolean
}

export interface NavGroup {
  label: string
  items: NavLink[]
}

export type NavEntry = NavGroup | NavLink

export const isNavGroup = (entry: NavEntry): entry is NavGroup => "items" in entry

export const PRODUCT_LINKS: NavLink[] = [
  {
    label: "Mileage tracker and logbook",
    href: "/mileage-tracker",
    description: "GPS trips, cents per km and the logbook method",
    icon: "route",
    tone: "primary",
  },
  {
    label: "Receipt scanner",
    href: "/receipt-scanner",
    description: "Photograph receipts; we read the total and GST",
    icon: "receipt-text",
    tone: "success",
  },
  {
    label: "Invoicing",
    href: "/invoicing",
    description: "Send tax invoices and see who has paid",
    icon: "file-text",
    tone: "violet",
  },
]

export const FREE_TOOL_LINKS: NavLink[] = [
  {
    label: "Cents per km calculator",
    href: "/cents-per-km-calculator",
    description: "This year's ATO rate and your claim",
    icon: "calculator",
    tone: "primary",
  },
  {
    label: "Pay calculator",
    href: "/pay-calculator",
    description: "Take-home pay after tax, Medicare and HELP",
    icon: "calculator",
    tone: "warning",
  },
  {
    label: "HECS-HELP repayment calculator",
    href: "/hecs-repayment-calculator",
    description: "Your compulsory repayment from your income",
    icon: "calculator",
    tone: "violet",
  },
  {
    label: "Medicare levy calculator",
    href: "/medicare-levy-calculator",
    description: "The levy and the low-income reduction",
    icon: "calculator",
    tone: "success",
  },
  {
    label: "ATO logbook template",
    href: "/ato-logbook-template",
    description: "Free CSV and PDF logbook for your car",
    icon: "file-spreadsheet",
    tone: "success",
  },
  {
    label: "Invoice generator",
    href: "/invoice-generator",
    description: "Make a PDF tax invoice, no account needed",
    icon: "file-text",
    tone: "violet",
  },
  {
    label: "GST calculator",
    href: "/gst-calculator",
    description: "Add or remove 10% GST from a price",
    icon: "calculator",
    tone: "warning",
  },
]

export const GUIDE_LINKS: NavLink[] = [
  {
    label: "Logbook method vs cents per km",
    href: "/logbook-method",
    description: "When the logbook method pays off",
    icon: "arrow-left-right",
    tone: "primary",
  },
  {
    label: "ATO logbook requirements",
    href: "/guides/ato-logbook-requirements",
    description: "What to record in the 12 weeks",
    icon: "clipboard-check",
    tone: "success",
  },
  {
    label: "ATO cents per km rates by year",
    href: "/guides/ato-cents-per-km-rate-history",
    description: "Every rate since 2022–23, with sources",
    icon: "chart-line",
    tone: "warning",
  },
  {
    label: "Tax invoice requirements",
    href: "/guides/tax-invoice-requirements",
    description: "What a tax invoice must show",
    icon: "file-check",
    tone: "violet",
  },
  {
    label: "Rideshare and delivery drivers",
    href: "/rideshare-drivers",
    description: "ABN, GST and car costs for drivers",
    icon: "car",
    tone: "primary",
  },
  {
    label: "Glossary",
    href: "/glossary",
    description: "Tax and logbook terms in plain English",
    icon: "book-open",
    tone: "muted",
  },
  {
    label: "Blog",
    href: "/blog",
    description: "News and rate changes",
    icon: "bell",
    tone: "muted",
  },
  {
    label: "All guides",
    href: "/guides",
    description: "Every guide and free tool in one list",
    footer: true,
  },
]

export const COMPARE_LINKS: NavLink[] = [
  {
    label: "Kestly vs Driversnote",
    href: "/compare/driversnote-alternative",
    description: "Free plan, price and features, each with a source",
  },
  {
    label: "Spreadsheet logbook or app?",
    href: "/compare/spreadsheet-logbook",
    description: "When a spreadsheet is enough for the 12 weeks",
  },
  {
    label: "All comparisons",
    href: "/compare",
    description: "Free plans of mileage and receipt apps at a glance",
  },
]

export const HEADER_NAV: NavEntry[] = [
  { label: "Products", items: PRODUCT_LINKS },
  { label: "Free tools", items: FREE_TOOL_LINKS },
  { label: "Guides", items: GUIDE_LINKS },
  { label: "Pricing", href: "/pricing" },
]

export interface FooterColumn {
  heading: string
  links: NavLink[]
}

const plain = (links: NavLink[]): NavLink[] => links.map(({ label, href }) => ({ label, href }))

export const FOOTER_COLUMNS: FooterColumn[] = [
  {
    heading: "Product",
    links: [
      ...plain(PRODUCT_LINKS),
      { label: "Pricing", href: "/pricing" },
      { label: "How it works", href: "/how-it-works" },
    ],
  },
  { heading: "Free tools", links: plain(FREE_TOOL_LINKS) },
  { heading: "Guides", links: plain(GUIDE_LINKS) },
  { heading: "Compare", links: plain(COMPARE_LINKS) },
  {
    heading: "Company",
    links: [
      { label: "FAQ", href: "/faq" },
      { label: "Contact", href: "/contact" },
      { label: "Security and privacy", href: "/trust" },
    ],
  },
]

export const LEGAL_LINKS: NavLink[] = [
  { label: "Privacy", href: "/legal/privacy" },
  { label: "Terms and Conditions", href: "/legal/terms-and-conditions" },
  { label: "Delete your account", href: "/legal/delete-account" },
]

/** The blog link is hidden while the blog has no posts; the route itself stays (noindex). */
export const withoutBlog = <T extends { href: string }>(links: T[], hasPosts: boolean): T[] =>
  hasPosts ? links : links.filter((l) => l.href !== "/blog")

/** Every internal href in the header, footer and legal list (for route and sitemap tests). */
export function allNavHrefs(): string[] {
  const header = HEADER_NAV.flatMap((e) => (isNavGroup(e) ? e.items : [e]))
  const footer = FOOTER_COLUMNS.flatMap((c) => c.links)
  return [...new Set([...header, ...footer, ...LEGAL_LINKS].map((l) => l.href))]
}
